import { Injectable } from '@angular/core'
import {
  RootState,
  Session,
  selectActiveSession,
  selectExternalTransactions,
  selectFocusedUIComponent,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { fromEvent } from 'rxjs'
import { take } from 'rxjs/operators'
import { DEFAULT_CONFIGURATION } from '../../models/copilot.model'
import { AvailableActionService } from '../available-action.service'
import { ExternalTransaction } from '@helios/common-api'

@Injectable({
  providedIn: 'root',
})
export class CopilotService {
  isReady = false
  focusedUIComponent: string | undefined = ''
  activeSession: Session | undefined
  sessionStarted = false
  externalTransactionsSelected: ExternalTransaction[] = []

  constructor(
    private readonly store: Store<RootState>,
    private readonly availableActionService: AvailableActionService,
  ) {}

  init() {
    fromEvent(window, 'CopilotInitialized')
      .pipe(take(1))
      .subscribe(() => {
        this.isReady = true
        this.setDigitalSpecialist()
        this.setSystemConfig()
      })

    this.store.select(selectFocusedUIComponent).subscribe(component => {
      this.focusedUIComponent = component

      this.setSystemConfig()
    })

    this.store.select(selectActiveSession).subscribe(session => {
      this.activeSession = session
      this.setSystemConfig()
    })

    this.store
      .select(selectExternalTransactions)
      .subscribe(externalTransactions => {
        this.externalTransactionsSelected = externalTransactions
        this.setSystemConfig()
      })
  }

  /**
   * @internal
   */
  setDigitalSpecialist() {
    if (!window.copilot) {
      return
    }

    window.copilot.setDigitalSpecialist('rubina', 'Helios')
  }

  /**
   * @internal
   */
  setSystemConfig() {
    if (!window.copilot) {
      return
    }

    try {
      window.copilot.setSystemPrompt({
        promptTemplateName: 'Helios:Context',
        variables: {
          activeSession: JSON.stringify(this.activeSession ?? {}, null, 2),
          focusedPanel: this.focusedUIComponent ?? '',
          extTranIds: JSON.stringify(
            this.externalTransactionsSelected.map(tran => tran.extTranId) ?? [],
            null,
            2,
          ),
        },
      })
    } catch (e) {
      console.error('Copilot: Unable to set system context', e)
      //The context will just not be set
    }

    if (!this.sessionStarted) {
      try {
        window.copilot.startSession({
          chatOptions: {
            tools: ['cosmos'],
          },
          modelInfo: DEFAULT_CONFIGURATION.modelInfo,
        })
      } catch (e) {
        console.error('Copilot: Unable to start session', e)
      }

      this.sessionStarted = true
    }

    window.copilot.setActionHandler(selectedAction =>
      this.availableActionService.handleAction(selectedAction),
    )
  }
}
